import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import styles from "./search.module.css";
import { searchService, type SearchHit } from "./searchService";

import { Button } from "@/components/Button";
import { PageHeader } from "@/components/PageHeader";
import { OPEN_CHAT_EVENT } from "@/features/chat/ChatWidget";
import manualStyles from "@/features/manual/manual.module.css";
import { sanitizeSnippet } from "@/features/manual/sanitize";

/** Hits grouped by section, sections ordered by their best-ranked hit. */
function groupBySection(
  hits: SearchHit[]
): Array<{ sectionSlug: string; sectionTitle: string; hits: SearchHit[] }> {
  const order: string[] = [];
  const groups = new Map<
    string,
    { sectionSlug: string; sectionTitle: string; hits: SearchHit[] }
  >();
  for (const hit of hits) {
    let group = groups.get(hit.sectionSlug);
    if (!group) {
      group = { sectionSlug: hit.sectionSlug, sectionTitle: hit.sectionTitle, hits: [] };
      groups.set(hit.sectionSlug, group);
      order.push(hit.sectionSlug);
    }
    group.hits.push(hit);
  }
  return order.map((slug) => groups.get(slug)!);
}

/** Site search: results grouped by section, snippet terms highlighted server-side. */
export function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q")?.trim() ?? "";

  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!q) {
      setHits([]);
      return;
    }
    setHits(null);
    setError(false);
    searchService
      .search(q)
      .then((r) => setHits(r.hits))
      .catch(() => setError(true));
  }, [q]);

  const groups = hits ? groupBySection(hits) : [];

  return (
    <div className={`${manualStyles.view} ${manualStyles.viewWide}`}>
      <PageHeader
        eyebrow="Search"
        title={
          q ? (
            <>
              Results for <span className={styles.queryText}>&ldquo;{q}&rdquo;</span>
            </>
          ) : (
            "Search OpsHub"
          )
        }
        intro={
          hits && q && hits.length > 0
            ? `${hits.length} result${hits.length === 1 ? "" : "s"} across ${groups.length} section${groups.length === 1 ? "" : "s"}.`
            : undefined
        }
      />

      {error && (
        <div className={manualStyles.emptyState}>Search couldn&rsquo;t be reached. Try again.</div>
      )}
      {!hits && !error && <div className={manualStyles.quiet}>Searching…</div>}
      {hits && q && hits.length === 0 && (
        <div className={manualStyles.emptyState}>
          Nothing in OpsHub matches that. Try different words — or ask the assistant, which can find
          answers keyword search misses.
          <div className={styles.emptyAction}>
            <Button
              variant="secondary"
              onClick={() =>
                window.dispatchEvent(new CustomEvent(OPEN_CHAT_EVENT, { detail: { question: q } }))
              }
            >
              Ask the assistant
            </Button>
          </div>
        </div>
      )}
      {!q && !error && hits && hits.length === 0 && (
        <div className={manualStyles.quiet}>Type a search above, or press “/” to jump in.</div>
      )}

      {groups.map((group) => (
        <section key={group.sectionSlug} className={styles.group}>
          <div className={styles.groupHeading}>{group.sectionTitle}</div>
          {group.hits.map((hit) => (
            <Link
              key={hit.pageId}
              to={`/manual/${hit.sectionSlug}/${hit.pageSlug}`}
              className={styles.result}
            >
              <div className={styles.resultTitle}>{hit.title}</div>
              <div
                className={styles.resultSnippet}
                // Snippets are mid-document fragments — close a cut-off one
                // with an ellipsis so it doesn't read as a finished sentence.
                dangerouslySetInnerHTML={{
                  __html:
                    sanitizeSnippet(hit.snippet) +
                    (/[.!?…]\s*$/.test(hit.snippet.replace(/<[^>]+>/g, "").trim()) ? "" : "…"),
                }}
              />
            </Link>
          ))}
        </section>
      ))}
    </div>
  );
}
